<template>
  <div>
    <v-progress-circular class="loader" v-show="loading" indeterminate color="primary"></v-progress-circular>
    <!--
       This tag sets base URL path for current HTML page https://www.w3schools.com/tags/tag_base.asp
       This helps to make all the links inside of an iFrame work properly
       (relative image src paths are resolved correctly to process.env.VUE_APP_URL)
    -->
    <base :href="basePath" />
    <iframe
      class="full-page-iframe"
      title="Preview page"
      @load="iframeLoaded($event)"
      ref="previewIframe"
      :srcdoc="pageHtml"></iframe>
  </div>
</template>

<script>
  import { textProvider } from "@/@core/http";

  export default {
    props: {
      domain: {
        type: String,
        required: true,
      },
      slug: {
        type: String,
        required: true,
      },
      version: {
        type: String,
      },
    },
    data() {
      return {
        pageHtml: "",
        loading: false,
        basePath: process.env.VUE_APP_API,
      };
    },
    async created() {
      const pageUrl = this.getPageUrl({ pageSlug: this.slug, version: this.version });
      await this.loadPage(pageUrl);
    },
    methods: {
      iframeLoaded() {
        const iFrameDocument = this.$refs.previewIframe.contentDocument;
        iFrameDocument.querySelectorAll("a").forEach(hrefElem => {
          hrefElem.addEventListener("click", e => this.iframeRedirectHandler(e, hrefElem));
        });
      },

      async iframeRedirectHandler(e, hrefElem) {
        e.preventDefault();
        // Prevent sending multiple same requests when link is clicked multiple times;
        if (this.loading) return;

        const link = hrefElem.attributes.href;
        /**
         * Redirect to home page
         */
        if (link.nodeValue === "/") {
          const url = this.getPageUrl({ homepage: true });

          return await this.loadPage(url);
        }
        /**
         * Redirect to category page
         * (Might have to amend the code block below in order for '/category/all' to work)
         */
        if (link.nodeValue.includes("category")) {
          // matches category and page parts of the url
          const pattern = /\/category(\/[^?]*)(?:\?page=(\d+))?/;
          const categorySlug = link.nodeValue.match(pattern)[1].substring(1);
          const page = link.nodeValue.match(pattern)[2];
          const url = this.getPageUrl({ categorySlug: categorySlug, page: page });

          return await this.loadPage(url);
        }

        /**
         * The rest of redirects are either to a Page page, or to an outside link
         */
        const pageSlug = link.nodeValue.split("/")[link.nodeValue.split("/").length - 1];

        /**
         * Checks if the parsed page slug has an extension like .com / .co.uk etc., which would denote that we're
         * trying to redirect to an outside link, which is not relevant to be handled in preview functionality
         */
        if (/\.[0-9a-z]{1,5}$/i.test(pageSlug)) {
          /**
           * No point to redirect to random outside urls (e.g. https://www.google.com)
           */
          console.warn("Trying to redirect to an outside link: ", pageSlug);

          return;
        }

        const url = this.getPageUrl({ pageSlug });

        return await this.loadPage(url);
      },

      async loadPage(pageUrl) {
        try {
          this.loading = true;
          const { data } = await textProvider.get(pageUrl);
          const pageHTML = data;
          this.pageHtml = pageHTML;
        } catch (err) {
          console.error(err);
        } finally {
          this.loading = false;
        }
      },

      /**
       * Different types of pages have different controller methods, we have to
       * send request accordingly
       */
      getPageUrl({ categorySlug, homepage, pageSlug, page, version } = {}) {
        if (homepage) {
          return `${process.env.VUE_APP_API}/api/preview/${this.domain}/homepage`;
        }

        if (categorySlug) {
          return `${process.env.VUE_APP_API}/api/preview/${this.domain}/category?slug=${categorySlug}&page=${
            page ?? 1
          }`;
        }

        if (pageSlug) {
          if (this.version) {
            return `${process.env.VUE_APP_API}/api/preview/${this.domain}/page?slug=${pageSlug}&version=${version}`;
          }

          return `${process.env.VUE_APP_API}/api/preview/${this.domain}/page?slug=${pageSlug}`;
        }
      },
    },
  };
</script>

<style scoped>
  .loader {
    position: absolute;
    z-index: 9999999;
    right: 10px;
    top: 21px;
  }

  .full-page-iframe {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    border: none;
    margin: 0;
    padding: 0;
    overflow: hidden;
    z-index: 999999;
  }
</style>
